import classNames from 'classnames';
import BaseAccordion from 'components/Accordions/BaseAccordion';
import React from 'react';
import { Button } from 'react-bootstrap';
import { BsCheck } from 'react-icons/bs';
import { FaBellSlash, FaRegEnvelope } from 'react-icons/fa';
import { GiSpeaker, GiSpeakerOff } from 'react-icons/gi';
import { toast } from 'react-toastify';
import type { Alarm } from '../../types/Alarms.types';
import { getAlarmStateLabel, isSilenced, shortenHeadline } from '../../utils/alarmUtils';
import { AlarmDropdown } from '../AlarmDropdown/AlarmDropdown';
import styles from './assets/AlarmsAccordion.module.css';

interface AlarmsAccordionProps {
  alarms: Alarm[];
  selectedRowIds: number[];
  setSelectedRowIds: React.Dispatch<React.SetStateAction<number[]>>;
  handleAcknowledge?: (alarm: Alarm) => Promise<boolean>;
  handleSilence?: (alarm: Alarm) => Promise<boolean>;
  handleUnsilence?: (alarm: Alarm) => Promise<boolean>;
  handleShare?: (alarm: Alarm) => void;
}

const deviceButtonStyle: React.CSSProperties = {
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 2px 4px rgba(0, 0, 0, 0.24)',
  position: 'relative',
  zIndex: 1,
};

export default function AlarmsAccordion({
  alarms,
  selectedRowIds,
  setSelectedRowIds,
  handleAcknowledge,
  handleSilence,
  handleUnsilence,
  handleShare,
}: AlarmsAccordionProps) {
  if (!alarms || alarms.length === 0) {
    return <div className="text-center py-5 text-muted">No alarms found.</div>;
  }

  return (
    <>
      {/* Mobile-only accordion container */}
      <div className="d-block d-lg-none mt-3">
        {alarms.map((alarm, idx) => (
          <div
            key={alarm.id}
            className={classNames(styles.alarmAccordion, idx !== alarms.length - 1 && 'mb-3')}
          >
            {/* Alarm item wrapper */}
            <BaseAccordion
              eventKey={String(idx)}
              bodyBackground="#436C9A"
              headerActions={
                <div className={styles.actions}>
                  {handleAcknowledge && (
                    <Button
                      variant="outline-light"
                      size="sm"
                      className={classNames(styles.actionButton, 'border')}
                      style={deviceButtonStyle}
                      title="Acknowledge"
                      onClick={async () => {
                        try {
                          const success = await handleAcknowledge(alarm);
                          if (success) toast.success(`Acknowledged ${alarm?.info?.type} alarm`);
                          else toast.error(`Failed to acknowledge ${alarm?.info?.type} alarm`);
                        } catch (err) {
                          toast.error(`Failed to acknowledge ${alarm?.info?.type} alarm`);
                        }
                      }}
                    >
                      <BsCheck />
                      <span className="small">Acknowledge</span>
                    </Button>
                  )}
                  {!isSilenced(alarm) && handleSilence && (
                    <Button
                      variant="outline-light"
                      size="sm"
                      className={classNames(styles.actionButton, 'border')}
                      style={deviceButtonStyle}
                      title="Silence"
                      onClick={async () => {
                        try {
                          const success = await handleSilence(alarm);
                          if (success) toast.success(`Silenced ${alarm?.info?.type} alarm`);
                          else toast.error(`Failed to silence ${alarm?.info?.type} alarm`);
                        } catch (err) {
                          toast.error(`Failed to silence ${alarm?.info?.type} alarm`);
                        }
                      }}
                    >
                      <GiSpeakerOff />
                      <span className="small">Silence</span>
                    </Button>
                  )}
                  {isSilenced(alarm) && handleUnsilence && (
                    <Button
                      variant="outline-light"
                      size="sm"
                      className={classNames(styles.actionButton, 'border')}
                      style={deviceButtonStyle}
                      title="Unsilence"
                      onClick={async () => {
                        try {
                          const success = await handleUnsilence(alarm);
                          if (success) toast.success(`Unsilenced ${alarm?.info?.type} alarm`);
                          else toast.error(`Failed to unsilence ${alarm?.info?.type} alarm`);
                        } catch (err) {
                          toast.error(`Failed to unsilence ${alarm?.info?.type} alarm`);
                        }
                      }}
                    >
                      <GiSpeaker />
                      <span className="small">Unsilence</span>
                    </Button>
                  )}
                  {handleShare && (
                    <Button
                      variant="outline-light"
                      size="sm"
                      className={classNames(styles.actionButton, 'border')}
                      style={deviceButtonStyle}
                      title="Share"
                      onClick={() => handleShare(alarm)}
                    >
                      <FaRegEnvelope />
                      <span className="small">Share</span>
                    </Button>
                  )}
                </div>
              }
              header={
                <div className={styles.header}>
                  {/* Accordion header */}
                  <div className={styles.summary}>
                    {/* Selection toggle */}
                    <div
                      data-testid={`alarm-select-${alarm.id}`}
                      className={classNames(
                        selectedRowIds.includes(alarm.id) ? 'circle-fill' : 'circle-border',
                        'center-content',
                        'me-2',
                        styles.selectionControl,
                      )}
                      style={
                        {
                          '--radius': selectedRowIds.includes(alarm.id) ? '16px' : '15px',
                        } as React.CSSProperties
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRowIds((current) =>
                          current.includes(alarm.id)
                            ? current.filter((id) => id !== alarm.id)
                            : [...current, alarm.id],
                        );
                      }}
                    >
                      {selectedRowIds.includes(alarm.id) && <BsCheck color={'black'} size={18} />}
                    </div>

                    {/* Headline & source */}
                    <div className={styles.alarmText}>
                      <div className={classNames(styles.alarmLabel, 'fw-semibold')}>
                        {shortenHeadline(alarm.headline || alarm.info?.type)}
                      </div>
                      <div className={classNames(styles.alarmType, 'small text-muted')}>
                        {alarm.info?.type || alarm.source}
                      </div>
                    </div>
                  </div>
                  <div className={styles.controls}>
                    <div className={styles.status}>
                      {/* Alarm state label */}
                      <div className="small text-white fw-bold">
                        {getAlarmStateLabel(alarm.state)}
                      </div>
                      {/* Silenced icon (only show when silence handlers are available) */}
                      {isSilenced(alarm) && (handleSilence || handleUnsilence) && (
                        <FaBellSlash title="Silenced" />
                      )}
                    </div>
                  </div>
                </div>
              }
            >
              {/* Accordion body: Alarm dropdown */}
              <div className="p-0">
                <div className="p-3">
                  <AlarmDropdown alarm={alarm} />
                </div>
              </div>
            </BaseAccordion>
          </div>
        ))}
      </div>
    </>
  );
}
